// eslint-disable-next-line boundaries/element-types
import { ComponentType } from '@angular/cdk/portal';
import { FormArray, FormControl, FormGroup } from '@angular/forms';

import { DialogConfigOptions, IconName } from '@core/user-interface-legacy';

import { TellentMember, TellentRole } from '../types';

export const MEMBER_LISTED_ONLY = 'member_listed_only';
export const EMPLOYEE_ROLE = 'employee';

export enum AddMemberAction {
  InviteMember = 'invite_member',
  AddEmployeeForPreonboarding = 'add_employee_for_preonboarding',
  AddEmployeeWithoutAccess = 'add_employee_without_access',
}

export interface AddMemberFlowChoiceCardI {
  heading: string;
  subheading: string;
  icon: IconName;
  action: AddMemberAction;
}

// -----------------------------------

export interface AddEmployeeForPreonboardingPayload {
  members: Array<{
    personalEmail: string;
    firstName: string;
    lastName: string;
  }>;
  seats: Array<{
    appGuid: string;
    access: boolean;
    roleExternalId: string;
  }>;
  sendInvitation: boolean;
}

export interface AddEmployeeWithoutAccessPayload {
  members: Array<{
    firstName: string;
    lastName: string;
    email: string;
    personalEmail: string;
  }>;
  seats: Array<{
    appGuid: string;
    access: boolean;
    roleExternalId: string;
  }>;
  sendInvitation: boolean;
}

export interface BulkUpdateMembersPayload {
  members: Array<{
    guid: string;
    email?: string;
    personalEmail?: string;
    extraPayload?: Record<string, any>;
  }>;
  seats: Array<{
    appGuid: string;
    access: boolean;
    roleExternalId: string | null;
  }>;
  abilities: string[];
  generateLogin?: boolean;
  sendInvitation?: boolean;
}

export interface UserInvitationFlowData {
  displayAutj?: boolean;
  isExistingMember?: boolean;
  payload?: TellentMember[];
  startPreonboarding?: boolean;
  inviteMethod?: InviteMethod | null;
  loginType?: LoginType | null;
  flowType?: UserInvitationFlow;
  completedWith?: UserInvitationCompletedWith;
}

export type UserInvitationCompletedWith = 'fill_out_profile' | 'start_preonboarding' | 'finish';

export interface UserInvitationDialogOutput {
  success: boolean;
  payload?: TellentMember[];
  completedWith?: UserInvitationCompletedWith;
}

export interface UserInvitationStepI {
  dialogComponent: ComponentType<any>;
  dialogConfig: DialogConfigOptions;
}

export enum InviteMethod {
  Email = 'email',
  Login = 'login',
}

export enum LoginType {
  Auto = 'auto',
  Custom = 'custom',
}

export type AddMemberForm = {
  inviteMethod: FormControl<InviteMethod | null>;
  loginType: FormControl<LoginType | null>;
  members: FormArray<FormGroup<MemberFormField<{}>>>;
  seats: FormGroup<{
    recruitee: FormGroup<SeatType>;
    kiwi: FormGroup<SeatType>;
  }>;
};

export type InviteExistingMemberForm = {
  inviteMethod: FormControl<InviteMethod | null>;
  email: FormControl<string | null>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  seats: FormGroup<{
    recruitee: FormGroup<SeatType>;
    kiwi: FormGroup<SeatType>;
  }>;
};

export type InviteExistingMemberForPreonboardingForm = {
  personalEmail: FormControl<string | null>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
};

export type MemberFormField<T> = {
  email: FormControl<string | null>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  login: FormControl<string | null>;
} & T;

export type SeatType = {
  toggle: FormControl<boolean | null>;
  appGuid: FormControl<string | null>;
  roleExternalId: FormControl<string | null>;
};

export enum UserInvitationFlow {
  AddMemberBasic = 'add_member_basic',
  AddMemberAdvanced = 'add_member_advanced',
  AddEmployeeForPreonboarding = 'add_employee_for_preonboarding',
  AddEmployeeWithoutAccess = 'add_employee_without_access',
  InviteExistingMember = 'invite_existing_member',
  InviteExistingMemberForPreonboarding = 'invite_existing_member_for_preonboarding',
}

export interface AddMemberFlowSelectionCardI {
  heading: string;
  subheading: string;
  icon: IconName;
  flowType?: UserInvitationFlow;
  shouldDisplay?: boolean;
}

export interface StepDefinition {
  step: UserInvitationStep;
  canMatch?: (data: Partial<UserInvitationFlowData>) => boolean;
  initialData?: any;
}

export type UserInvitationStep =
  | 'add_member'
  | 'add_member_new'
  | 'add_employee_for_preonboarding'
  | 'add_employee_without_access'
  | 'assign_members_to_jobs'
  | 'member_added_success'
  | 'download_credentials'
  | 'subdomain_information'
  | 'journey_promo_dialog'
  | 'invite_existing_member'
  | 'invite_existing_member_for_preonboarding';

export interface TellentBrandsDefaultRoles {
  recruitee: TellentRole | undefined;
  kiwi: TellentRole | undefined;
}

export enum LoginMethod {
  Email = 'email',
  Username = 'username',
}
